import * as React from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * Service worker do Hub instalável (Onda 6.2).
 *
 * O SW guarda só o app e os assets — nunca resposta da API (ver `vite.config.ts`).
 * Versão nova nunca troca sozinha: a pessoa pode estar no meio de uma nota. O SW
 * novo fica esperando e o Hub mostra "Nova versão disponível — Atualizar"; só o
 * clique ativa e recarrega.
 *
 * O navegador só procura SW novo ao navegar, e o Hub é SPA: sem as checagens
 * abaixo, quem deixa a aba aberta o dia todo nunca veria o aviso.
 */
const CHECK_EVERY_MS = 60 * 60 * 1000

export interface ServiceWorkerUpdate {
  /** Há versão nova instalada, esperando o clique. */
  needRefresh: boolean
  /** Ativa a versão nova e recarrega a página. */
  update: () => void
  /** Esconde o aviso até a próxima versão (ou a próxima abertura). */
  dismiss: () => void
}

export function useServiceWorkerUpdate(): ServiceWorkerUpdate {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return
      const check = () => {
        // Sem rede ou com outra instalação em curso, a checagem só gera erro.
        if (registration.installing || !navigator.onLine) return
        registration.update().catch(() => undefined)
      }
      window.setInterval(check, CHECK_EVERY_MS)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
    },
    onRegisterError() {
      // Sem SW o Hub funciona igual, só não é instalável. Nada a mostrar.
    },
  })

  const update = React.useCallback(() => {
    void updateServiceWorker(true)
  }, [updateServiceWorker])
  const dismiss = React.useCallback(() => setNeedRefresh(false), [setNeedRefresh])

  return { needRefresh, update, dismiss }
}
